import React, { useState } from 'react';
import styled from 'styled-components';

// Types for Plan Comparison
export interface TradePlan {
  planId: string;
  name: string;
  description: string;
  overallScore: number;
  constraintScore: number;
  liquidityScore: number;
  costScore: number;
  numberOfTrades: number;
  estimatedCostBps: number;
  turnoverPercent: number;
  trades: ProposedTrade[];
  constraintResults: ConstraintResult[];
  tradeOffs: TradeOff[];
  beforeState: PortfolioImpact;
  afterState: PortfolioImpact;
  deltaState: PortfolioImpact;
  reasoning: string[];
}

export interface ProposedTrade {
  tradeId: string;
  side: 'BUY' | 'SELL';
  securityId: string;
  cusip: string;
  securityName: string;
  ticker: string;
  quantity: number;
  notional: number;
  price: number;
  duration: number;
  yield: number;
  creditRating: string;
  sector: string;
  rationale: string;
}

export interface ConstraintResult {
  constraintId: string;
  constraintName: string;
  satisfied: boolean;
  currentValue: number;
  proposedValue: number;
  targetValue: number;
  explanation: string;
}

export interface TradeOff {
  metric: string;
  description: string;
  thisValue: number;
  alternativeValue: number;
  comparison: 'better' | 'worse' | 'similar';
  severity: 'MINOR' | 'MODERATE' | 'SIGNIFICANT';
}

export interface PortfolioImpact {
  totalMarketValue: number;
  duration: number;
  averageCreditRating: number;
  yield: number;
  sectorExposures: Record<string, number>;
}

export interface ComparisonRow {
  metric: string;
  values: number[];
  betterDirection: 'higher' | 'lower' | 'depends';
}

interface PlanComparisonProps {
  plans: TradePlan[];
  comparisonMatrix: ComparisonRow[];
  onSelectPlan: (planId: string) => void;
  onExecutePlan: (planId: string) => void;
  selectedPlanId?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: #0f0f1a;
  border-radius: 12px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 4px 0 0;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.05);
  padding: 4px;
  border-radius: 8px;
`;

const ViewButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: ${props => props.$active ? '#3b82f6' : 'transparent'};
  color: ${props => props.$active ? '#ffffff' : '#9ca3af'};

  &:hover {
    background: ${props => props.$active ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
`;

const PlanCard = styled.div<{ $selected: boolean; $recommended: boolean }>`
  background: #1a1a2e;
  border-radius: 12px;
  border: 2px solid ${props => {
    if (props.$selected) return '#3b82f6';
    if (props.$recommended) return '#22c55e';
    return '#2a2a4a';
  }};
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${props => props.$selected ? '#3b82f6' : '#4b5563'};
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const PlanHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #2a2a4a;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const PlanTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 4px;
`;

const PlanDescription = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
`;

const ScoreBadge = styled.div<{ $score: number }>`
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  background: ${props => {
    if (props.$score >= 80) return 'rgba(34, 197, 94, 0.2)';
    if (props.$score >= 60) return 'rgba(59, 130, 246, 0.2)';
    if (props.$score >= 40) return 'rgba(245, 158, 11, 0.2)';
    return 'rgba(239, 68, 68, 0.2)';
  }};
  color: ${props => {
    if (props.$score >= 80) return '#22c55e';
    if (props.$score >= 60) return '#3b82f6';
    if (props.$score >= 40) return '#f59e0b';
    return '#ef4444';
  }};
`;

const RecommendedBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: rgba(34, 197, 94, 0.2);
  color: #22c55e;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 8px;
`;

const PlanMetrics = styled.div`
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricLabel = styled.span`
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
`;

const MetricValue = styled.span<{ $highlight?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$highlight ? '#3b82f6' : '#ffffff'};
`;

const ImpactSection = styled.div`
  padding: 16px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid #2a2a4a;
`;

const ImpactTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  margin: 0 0 12px;
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const ImpactItem = styled.div<{ $positive?: boolean; $negative?: boolean }>`
  padding: 8px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  text-align: center;
`;

const ImpactValue = styled.div<{ $change: number }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => {
    if (props.$change > 0) return '#22c55e';
    if (props.$change < 0) return '#ef4444';
    return '#9ca3af';
  }};
`;

const ImpactLabel = styled.div`
  font-size: 10px;
  color: #6b7280;
  margin-top: 2px;
`;

const TradeOffSection = styled.div`
  padding: 16px;
  border-top: 1px solid #2a2a4a;
`;

const TradeOffTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  margin: 0 0 12px;
`;

const TradeOffList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TradeOffItem = styled.div<{ $comparison: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: ${props => {
    if (props.$comparison === 'better') return 'rgba(34, 197, 94, 0.1)';
    if (props.$comparison === 'worse') return 'rgba(239, 68, 68, 0.1)';
    return 'rgba(255, 255, 255, 0.02)';
  }};
  border-radius: 6px;
`;

const TradeOffIcon = styled.span<{ $comparison: string }>`
  font-size: 12px;
  color: ${props => {
    if (props.$comparison === 'better') return '#22c55e';
    if (props.$comparison === 'worse') return '#ef4444';
    return '#9ca3af';
  }};
`;

const TradeOffText = styled.span`
  font-size: 12px;
  color: #e5e7eb;
  flex: 1;
`;

const ActionBar = styled.div`
  padding: 16px;
  border-top: 1px solid #2a2a4a;
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: ${props => props.$primary ? 'none' : '1px solid #3b82f6'};
  background: ${props => props.$primary ? '#3b82f6' : 'transparent'};
  color: ${props => props.$primary ? '#ffffff' : '#3b82f6'};

  &:hover {
    background: ${props => props.$primary ? '#2563eb' : 'rgba(59, 130, 246, 0.1)'};
  }
`;

// Comparison Table View
const ComparisonTable = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  border-bottom: 1px solid #2a2a4a;
  background: #1a1a2e;

  &:first-child {
    border-radius: 8px 0 0 0;
  }

  &:last-child {
    border-radius: 0 8px 0 0;
  }
`;

const TableRow = styled.tr`
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const TableCell = styled.td<{ $highlight?: boolean; $best?: boolean }>`
  padding: 12px 16px;
  font-size: 13px;
  color: ${props => props.$highlight ? '#3b82f6' : '#e5e7eb'};
  border-bottom: 1px solid #2a2a4a;
  background: ${props => props.$best ? 'rgba(34, 197, 94, 0.1)' : 'transparent'};
`;

const MetricCell = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
  border-bottom: 1px solid #2a2a4a;
`;

const formatNumber = (value: number, decimals: number = 2): string => {
  return value.toFixed(decimals);
};

const formatCurrency = (value: number): string => {
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const formatChange = (value: number, suffix: string = ''): string => {
  const prefix = value >= 0 ? '+' : '';
  return `${prefix}${formatNumber(value)}${suffix}`;
};

export const PlanComparison: React.FC<PlanComparisonProps> = ({
  plans,
  comparisonMatrix,
  onSelectPlan,
  onExecutePlan,
  selectedPlanId,
}) => {
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);

  const getBestValueIndex = (row: ComparisonRow): number => {
    if (row.betterDirection === 'depends') return -1;

    const values = row.values;
    if (row.betterDirection === 'higher') {
      return values.indexOf(Math.max(...values));
    } else {
      return values.indexOf(Math.min(...values));
    }
  };

  const getTradeOffIcon = (comparison: string): string => {
    switch (comparison) {
      case 'better': return '✓';
      case 'worse': return '✗';
      default: return '≈';
    }
  };

  return (
    <Container>
      <Header>
        <div>
          <Title>Alternative Plans</Title>
          <Subtitle>
            {plans.length} plans generated - compare trade-offs and select the best option
          </Subtitle>
        </div>
        <ViewToggle>
          <ViewButton $active={view === 'cards'} onClick={() => setView('cards')}>
            Cards
          </ViewButton>
          <ViewButton $active={view === 'table'} onClick={() => setView('table')}>
            Table
          </ViewButton>
        </ViewToggle>
      </Header>

      {view === 'cards' ? (
        <PlansGrid>
          {plans.map((plan, index) => (
            <PlanCard
              key={plan.planId}
              $selected={selectedPlanId === plan.planId}
              $recommended={index === 0}
              onClick={() => onSelectPlan(plan.planId)}
            >
              <PlanHeader>
                <div>
                  <PlanTitle>
                    {plan.name}
                    {index === 0 && <RecommendedBadge>Recommended</RecommendedBadge>}
                  </PlanTitle>
                  <PlanDescription>{plan.description}</PlanDescription>
                </div>
                <ScoreBadge $score={plan.overallScore}>
                  {Math.round(plan.overallScore)}
                </ScoreBadge>
              </PlanHeader>

              <PlanMetrics>
                <MetricItem>
                  <MetricLabel>Trades</MetricLabel>
                  <MetricValue>{plan.numberOfTrades}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Cost</MetricLabel>
                  <MetricValue>{formatNumber(plan.estimatedCostBps, 1)} bps</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Turnover</MetricLabel>
                  <MetricValue>{formatNumber(plan.turnoverPercent, 1)}%</MetricValue>
                </MetricItem>
                <MetricItem>
                  <MetricLabel>Liquidity</MetricLabel>
                  <MetricValue $highlight>{formatNumber(plan.liquidityScore, 0)}</MetricValue>
                </MetricItem>
              </PlanMetrics>

              <ImpactSection>
                <ImpactTitle>Portfolio Impact</ImpactTitle>
                <ImpactGrid>
                  <ImpactItem>
                    <ImpactValue $change={plan.deltaState.duration}>
                      {formatChange(plan.deltaState.duration, 'y')}
                    </ImpactValue>
                    <ImpactLabel>Duration</ImpactLabel>
                  </ImpactItem>
                  <ImpactItem>
                    <ImpactValue $change={-plan.deltaState.averageCreditRating}>
                      {formatChange(plan.deltaState.averageCreditRating)}
                    </ImpactValue>
                    <ImpactLabel>Credit</ImpactLabel>
                  </ImpactItem>
                  <ImpactItem>
                    <ImpactValue $change={plan.deltaState.yield * 10000}>
                      {formatChange(plan.deltaState.yield * 10000, 'bp')}
                    </ImpactValue>
                    <ImpactLabel>Yield</ImpactLabel>
                  </ImpactItem>
                </ImpactGrid>
              </ImpactSection>

              {plan.tradeOffs.length > 0 && (
                <TradeOffSection>
                  <TradeOffTitle>Trade-offs vs Best</TradeOffTitle>
                  <TradeOffList>
                    {plan.tradeOffs.slice(0, 3).map((tradeOff, i) => (
                      <TradeOffItem key={i} $comparison={tradeOff.comparison}>
                        <TradeOffIcon $comparison={tradeOff.comparison}>
                          {getTradeOffIcon(tradeOff.comparison)}
                        </TradeOffIcon>
                        <TradeOffText>
                          {tradeOff.metric}: {formatNumber(tradeOff.thisValue)} vs {formatNumber(tradeOff.alternativeValue)}
                        </TradeOffText>
                      </TradeOffItem>
                    ))}
                  </TradeOffList>
                </TradeOffSection>
              )}

              <ActionBar>
                <ActionButton onClick={() => setExpandedPlan(expandedPlan === plan.planId ? null : plan.planId)}>
                  {expandedPlan === plan.planId ? 'Hide Details' : 'View Details'}
                </ActionButton>
                <ActionButton $primary onClick={() => onExecutePlan(plan.planId)}>
                  Execute Plan
                </ActionButton>
              </ActionBar>
            </PlanCard>
          ))}
        </PlansGrid>
      ) : (
        <ComparisonTable>
          <Table>
            <thead>
              <tr>
                <TableHeader>Metric</TableHeader>
                {plans.map((plan, index) => (
                  <TableHeader key={plan.planId}>
                    {plan.name}
                    {index === 0 && ' ⭐'}
                  </TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonMatrix.map((row, rowIndex) => {
                const bestIndex = getBestValueIndex(row);
                return (
                  <TableRow key={rowIndex}>
                    <MetricCell>{row.metric}</MetricCell>
                    {row.values.map((value, colIndex) => (
                      <TableCell
                        key={colIndex}
                        $best={colIndex === bestIndex}
                        $highlight={selectedPlanId === plans[colIndex]?.planId}
                      >
                        {formatNumber(value)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </ComparisonTable>
      )}
    </Container>
  );
};

export default PlanComparison;
